// components/layout/Header.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Page } from '../../types';
import { navItems } from '../../data/constants';
import { isRollout0Feature, contactInfo } from '../../config/featureFlags';
import NavLink from './NavLink';
import { trackPhoneClick } from '../../utils/tracking';

// Mapping from Page type to route paths
const pageToRoute: Record<Page, string> = {
  'Home': '/',
  'About': '/about',
  'Services': '/services',
  'OurWork': '/our-work',
  'Gallery': '/gallery',
  'Blog': '/blog',
  'Testimonials': '/testimonials',
  'Contact': '/contact',
  'RequestQuote': '/request-quote'
};

// Mapping from route paths to Page type
const routeToPage: Record<string, Page> = {
  '/': 'Home',
  '/about': 'About',
  '/services': 'Services',
  '/our-work': 'OurWork',
  '/gallery': 'Gallery',
  '/blog': 'Blog',
  '/testimonials': 'Testimonials',
  '/contact': 'Contact',
  '/request-quote': 'RequestQuote'
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const currentPage = routeToPage[location.pathname]
    || Object.entries(routeToPage).find(([path]) => path !== '/' && location.pathname.startsWith(path + '/'))?.[1]
    || 'Home';
  const hasEmergencyBanner = isRollout0Feature('emergencyBanner');

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
      hasEmergencyBanner ? 'top-10' : 'top-0'
    } ${
      isScrolled
        ? 'bg-slate-900/95 backdrop-blur-md shadow-2xl'
        : 'bg-gradient-to-r from-slate-900/90 to-slate-800/90 backdrop-blur-sm'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0 group">
            <button
              onClick={() => navigate('/')}
              className="flex flex-col items-start transition-all duration-300 group-hover:scale-105"
            >
              <span className="text-3xl font-bold bg-gradient-to-r from-lime-400 to-lime-500 bg-clip-text text-transparent">
                Lemay Roofing
              </span>
              <span className="text-xs text-gray-400 -mt-1 ml-1 opacity-80">
                Avon Park, FL
              </span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {isRollout0Feature('phoneInHeader') && (
              <a
                href={`tel:${contactInfo.phoneRaw}`}
                onClick={trackPhoneClick}
                className="flex items-center gap-2 px-4 py-2 mr-2 text-white hover:text-lime-400 transition-colors group"
              >
                <span className="text-2xl group-hover:scale-110 transition-transform">📞</span>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400">Call Us Now</span>
                  <span className="font-bold text-lg">{contactInfo.phone}</span>
                </div>
              </a>
            )}
            {navItems.map(item => (
              <NavLink
                key={item.page}
                page={item.page}
                currentPage={currentPage}
                isButton={item.isButton}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-lime-500 transition-all duration-200"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`bg-current block h-0.5 w-6 rounded-sm transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'
                }`} />
                <span className={`bg-current block h-0.5 w-6 rounded-sm transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                }`} />
                <span className={`bg-current block h-0.5 w-6 rounded-sm transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'
                }`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div className={`lg:hidden transition-all duration-300 ease-out ${
        isMobileMenuOpen
          ? 'max-h-screen opacity-100'
          : 'max-h-0 opacity-0 pointer-events-none'
      } overflow-hidden`}>
        <div className="bg-slate-900/98 backdrop-blur-md border-t border-slate-700/50">
          <div className="container mx-auto px-4 py-6">
            {isRollout0Feature('phoneInHeader') && (
              <a
                href={`tel:${contactInfo.phoneRaw}`}
                onClick={trackPhoneClick}
                className="flex items-center justify-center gap-3 p-4 mb-4 bg-lime-600 hover:bg-lime-700 text-white rounded-xl font-bold text-lg transition-colors shadow-lg"
              >
                <span className="text-2xl">📞</span>
                <span>Call: {contactInfo.phone}</span>
              </a>
            )}
            <nav className="flex flex-col space-y-2">
              {navItems.map(item => (
                <button
                  key={item.page}
                  onClick={() => {
                    navigate(pageToRoute[item.page]);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-left p-4 rounded-xl font-medium transition-all duration-200 ${
                    currentPage === item.page
                      ? item.isButton
                        ? 'bg-gradient-to-r from-lime-500 to-lime-600 text-white shadow-lg'
                        : 'bg-lime-500/20 text-lime-400 border border-lime-500/30'
                      : item.isButton
                        ? 'bg-gradient-to-r from-lime-500/80 to-lime-600/80 text-white hover:from-lime-500 hover:to-lime-600'
                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
